import type { CSSProperties } from 'vue'

import TextElement from './TextElement.vue'

import type { FlashcardSize, TextElementInput } from '../types'
import type { ElementDefinition } from '../runtime/elementDefinition'
import { invertColor, toRgb } from '../runtime/style'
import { scaleHeight, scaleWidth } from '../runtime/math'
import { normalizeTextContentToPlain } from '../../shared/textContent'

const estimateTextBox = (input: TextElementInput, size: FlashcardSize) => {
  // If explicit sizing is set, prefer that for the selection box.
  if (typeof input.width === 'number' && typeof input.height === 'number') {
    const widthPx = scaleWidth(input.width, size)
    const heightPx = scaleHeight(input.height, size)
    if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx <= 0 || heightPx <= 0) {
      return null
    }
    return { widthPx, heightPx }
  }

  const content =
    typeof input.content === 'string' ? normalizeTextContentToPlain(input.content) : ''
  const lines = content.split('\n')
  const longest = Math.max(1, ...lines.map((line) => line.length))
  const lineCount = Math.max(1, lines.length)

  const fontSizeBase = typeof input.font?.size === 'number' ? input.font.size : 18
  const lineHeight =
    typeof input.font?.lineHeight === 'number' && input.font.lineHeight > 0
      ? input.font.lineHeight
      : 1.2

  const fontSizePx = scaleHeight(fontSizeBase, size)
  if (!Number.isFinite(fontSizePx) || fontSizePx <= 0) return null

  const approxWidthPx = fontSizePx * longest * 0.6
  const approxHeightPx = fontSizePx * lineCount * lineHeight
  return {
    widthPx: Math.max(0, approxWidthPx),
    heightPx: Math.max(0, approxHeightPx),
  }
}

const definition: ElementDefinition<TextElementInput> = {
  type: 'text',
  component: TextElement,
  estimateBox: (input, size) => estimateTextBox(input, size),
  toProps: (input, ctx) => {
    if (!input.font || typeof input.content !== 'string') {
      return null
    }

    const decodedContent = normalizeTextContentToPlain(input.content)

    const fontSizePx = scaleHeight(input.font.size, ctx.size)
    if (!Number.isFinite(fontSizePx) || fontSizePx <= 0) {
      return null
    }

    const resolvedColor = ctx.theme.darkMode
      ? (input.font.darkColor ?? invertColor(input.font.color))
      : input.font.color

    const style: CSSProperties = {
      ...ctx.baseStyle,
      color: toRgb(resolvedColor),
      fontSize: `${fontSizePx}px`,
      display: 'inline-block',
      lineHeight: input.font.lineHeight ?? 1.2,
    }

    // Optional explicit box sizing.
    const hasExplicitWidth = typeof input.width === 'number'
    const hasExplicitHeight = typeof input.height === 'number'

    if (typeof input.width === 'number') {
      const widthPx = scaleWidth(input.width, ctx.size)
      if (Number.isFinite(widthPx) && widthPx > 0) style.width = `${widthPx}px`
    }
    if (typeof input.height === 'number') {
      const heightPx = scaleHeight(input.height, ctx.size)
      if (Number.isFinite(heightPx) && heightPx > 0) style.height = `${heightPx}px`
    }

    // If the user didn't set a width, don't let the browser "shrink-to-fit" based on
    // remaining space to the card edge (which causes unexpected wrapping).
    if (!hasExplicitWidth) {
      ;(style as any).width = 'max-content'
      ;(style as any).maxWidth = 'none'
    }

    // Alignment
    const align = input.align ?? 'left'
    ;(style as CSSProperties & { textAlign?: string }).textAlign = align

    // Elementor-like "blocked" text: justify including the last line.
    if (align === 'justify') {
      ;(style as any).textAlignLast = 'justify'
      ;(style as any).textJustify = 'inter-word'
    }

    // Vertical alignment only makes sense when a fixed height is provided.
    if (typeof style.height === 'string') {
      style.display = 'flex'
      style.flexDirection = 'column'
      style.justifyContent =
        input.vAlign === 'bottom' ? 'flex-end' : input.vAlign === 'middle' ? 'center' : 'flex-start'
    }

    if (input.font.family) {
      style.fontFamily = input.font.family
    }
    if (input.font.weight) {
      style.fontWeight = input.font.weight
    }
    return {
      content: decodedContent,
      style,
    }
  },
}

export default definition
